import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// ---------------------------------------------------------------------------
// Scroll-Verhalten beim Seitenwechsel.
//
//  • Neue Seite (Klick auf einen Link) → nach oben. Ohne das behielt der Browser
//    die Position der vorigen Seite: Wer aus dem Fuß der Startseite auf
//    "Finanzierung" klickte, landete mitten in der neuen Seite.
//  • Zurück/Vorwärts → an die Stelle, an der man die Seite verlassen hat. Genau
//    dafür wird die Position selbst gemerkt: Die browsereigene Wiederherstellung
//    (scrollRestoration "auto") greift zu früh — die Trefferliste rendert erst
//    nach dem Datenabruf, der Browser fand die Seite noch zu kurz und landete
//    ~200px zu hoch, ohne später nachzuziehen. Deshalb "manual" + eigener Retry.
//  • Anker-Links (/wertermittlung#wert-ablauf) → zum Anker statt nach oben.
//
// Nur pathname/hash triggern. Filterwechsel in der Suche ändern lediglich die
// Query-Parameter — die Liste soll dabei stehen bleiben.
// ---------------------------------------------------------------------------

export default function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map());
  // Während wir selbst scrollen, darf der scroll-Listener die gemerkte Position
  // nicht mit einem Zwischenstand überschreiben (die Seite ist dann noch zu kurz).
  const restoring = useRef(false);
  // Letzter pathname, NICHT location.key: Ein Filterwechsel in der Suche ändert nur
  // die Query, bekommt aber trotzdem einen neuen key — daran gehängt sprang die
  // Trefferliste bei jedem Filterklick nach oben.
  const prevPath = useRef(location.pathname);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Aktuelle Position laufend zum aktuellen History-Eintrag merken.
  useEffect(() => {
    const key = location.key;
    const onScroll = () => {
      if (!restoring.current) positions.current.set(key, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.key]);

  useEffect(() => {
    const samePage = location.pathname === prevPath.current;
    prevPath.current = location.pathname;

    if (navType === "POP") {
      const y = positions.current.get(location.key);
      // Kein Eintrag: frisch geladene Seite (F5) — dann oben bleiben.
      if (!y) return undefined;
      restoring.current = true;
      let frames = 0;
      let raf = 0;
      const restore = () => {
        window.scrollTo(0, y);
        // Nachfassen, bis der Inhalt da und die Seite hoch genug ist.
        if (Math.abs(window.scrollY - y) > 2 && frames++ < 40) {
          raf = requestAnimationFrame(restore);
        } else {
          restoring.current = false;
        }
      };
      restore();
      return () => {
        cancelAnimationFrame(raf);
        restoring.current = false;
      };
    }

    if (location.hash) {
      let tries = 0;
      let t = 0;
      const jump = () => {
        const el = document.getElementById(location.hash.slice(1));
        if (el) { el.scrollIntoView(); return; }
        // Die Route lädt lazy nach — der Anker kann noch fehlen, also nachfassen.
        if (tries++ < 20) t = setTimeout(jump, 100);
      };
      jump();
      return () => clearTimeout(t);
    }

    // Gleiche Seite, nur andere Query (Filter, Ansichtswechsel) → stehen bleiben.
    if (samePage) return undefined;

    window.scrollTo(0, 0);
    return undefined;
  }, [location.key, location.pathname, location.hash, navType]);

  return null;
}
